import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Car, ClipboardList, Clock, CheckCircle, Wrench, RefreshCw, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("vywork_dashboard_tab") || "alle";
  });

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    localStorage.setItem("vywork_dashboard_tab", tabId);
  };

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/api/dashboard/stats");
      setStats(response.data);
    } catch (err) {
      console.error("Stats error:", err);
      setError("Fehler beim Laden");
      toast.error("Fehler beim Laden der Statistiken");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      offen: "bg-blue-100 text-blue-800",
      in_bearbeitung: "bg-orange-100 text-orange-800",
      erledigt: "bg-green-100 text-green-800"
    };
    const labels = {
      offen: "Offen",
      in_bearbeitung: "In Bearbeitung",
      erledigt: "Erledigt"
    };
    return (
      <Badge className={`${styles[status]} font-medium`} data-testid={`status-badge-${status}`}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4" data-testid="dashboard-error">
        <p className="text-muted-foreground">{error}</p>
        <Button onClick={fetchStats} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Erneut versuchen
        </Button>
      </div>
    );
  }

  const statCards = [
    { 
      title: "Kunden", 
      value: stats?.total_customers || 0, 
      icon: Users, 
      color: "text-primary",
      bgColor: "bg-primary/10",
      link: "/kunden"
    },
    { 
      title: "Fahrzeuge", 
      value: stats?.total_vehicles || 0, 
      icon: Car, 
      color: "text-green-600",
      bgColor: "bg-green-100",
      link: "/fahrzeuge"
    },
    { 
      title: "Offene Aufträge", 
      value: stats?.open_orders || 0, 
      icon: ClipboardList, 
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      link: "/auftraege?status=offen"
    },
    { 
      title: "In Bearbeitung", 
      value: stats?.in_progress_orders || 0, 
      icon: Clock, 
      color: "text-orange-600",
      bgColor: "bg-orange-100",
      link: "/auftraege?status=in_bearbeitung"
    },
    { 
      title: "Erledigt", 
      value: stats?.completed_orders || 0, 
      icon: CheckCircle, 
      color: "text-green-600",
      bgColor: "bg-green-100",
      link: "/auftraege?status=erledigt"
    },
  ];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-1 text-sm lg:text-base">
            Willkommen zurück, {user?.name}!
          </p>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Wrench className="w-4 h-4 lg:w-5 lg:h-5" />
          <span className="text-xs lg:text-sm">{new Date().toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
      </div>

      {/* Stats Grid - Clickable Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4" data-testid="stats-grid">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className="card-hover border shadow-sm cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98]" 
            onClick={() => navigate(stat.link)}
            data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-3 lg:p-4">
              <div className="flex items-center gap-2 lg:gap-3">
                <div className={`w-8 h-8 lg:w-10 lg:h-10 ${stat.bgColor} rounded-sm flex items-center justify-center flex-shrink-0`}>
                  <stat.icon className={`w-4 h-4 lg:w-5 lg:h-5 ${stat.color}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xl lg:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                    {stat.value}
                  </p>
                  <p className="text-[10px] lg:text-xs text-muted-foreground uppercase tracking-wider truncate">
                    {stat.title}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground hidden sm:block" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Button 
          className="btn-primary h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate("/kunden")}
          data-testid="quick-action-customers"
        >
          <Users className="w-6 h-6" />
          <span>Kunden verwalten</span>
        </Button>
        <Button 
          className="btn-primary h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate("/fahrzeuge")}
          data-testid="quick-action-vehicles"
        >
          <Car className="w-6 h-6" />
          <span>Fahrzeuge verwalten</span>
        </Button>
        <Button 
          className="btn-secondary h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate("/auftraege?new=true")}
          data-testid="quick-action-new-order"
        >
          <ClipboardList className="w-6 h-6" />
          <span>Neuer Auftrag</span>
        </Button>
      </div>

      {/* Recent Orders with Tabs */}
      <Card className="border shadow-sm" data-testid="recent-orders-card">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                Aufträge
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-primary"
                onClick={() => navigate("/auftraege")}
              >
                Zur Auftragsverwaltung
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            {/* Status Tabs */}
            <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1" data-testid="order-status-tabs">
              {[
                { id: "alle", label: "Alle", count: stats?.recent_orders?.length || 0 },
                { id: "offen", label: "Offen", count: stats?.recent_orders?.filter(o => o.status === "offen").length || 0, color: "text-blue-600" },
                { id: "in_bearbeitung", label: "In Bearbeitung", count: stats?.recent_orders?.filter(o => o.status === "in_bearbeitung").length || 0, color: "text-orange-600" },
                { id: "erledigt", label: "Erledigt", count: stats?.recent_orders?.filter(o => o.status === "erledigt").length || 0, color: "text-green-600" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-all flex items-center gap-2 ${
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <span>{tab.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-background"
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            // Filter orders based on active tab
            const filteredOrders = activeTab === "alle" 
              ? stats?.recent_orders 
              : stats?.recent_orders?.filter(o => o.status === activeTab);
            
            if (filteredOrders?.length > 0) {
              return (
                <div className="space-y-3">
                  {filteredOrders.map((order) => (
                    <div
                      key={order.id}
                      className={`p-4 rounded-sm border-l-4 bg-card cursor-pointer hover:bg-muted/50 transition-colors ${
                        order.status === 'offen' ? 'border-blue-500' :
                        order.status === 'in_bearbeitung' ? 'border-orange-500' :
                        'border-green-500'
                      }`}
                      onClick={() => navigate(`/auftraege?order=${order.id}`)}
                      data-testid={`recent-order-${order.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="space-y-1 min-w-0">
                          <p className="font-semibold text-foreground truncate">{order.description || "Auftrag"}</p>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1 truncate">
                              <Car className="w-4 h-4 flex-shrink-0" />
                              {order.vehicle_info}
                            </span>
                            <span className="flex items-center gap-1 truncate">
                              <Users className="w-4 h-4 flex-shrink-0" />
                              {order.customer_name}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {getStatusBadge(order.status)}
                          <span className="text-xs text-muted-foreground">
                            {new Date(order.created_at).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            } else {
              return (
                <div className="text-center py-8 text-muted-foreground" data-testid="no-orders-message">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>
                    {activeTab === "alle" 
                      ? "Keine Aufträge vorhanden" 
                      : `Keine ${activeTab === "offen" ? "offenen" : activeTab === "in_bearbeitung" ? "laufenden" : "erledigten"} Aufträge`
                    }
                  </p>
                  {activeTab === "alle" && (
                    <Button 
                      className="mt-4 btn-primary"
                      onClick={() => navigate("/auftraege")}
                    >
                      Ersten Auftrag erstellen
                    </Button>
                  )}
                </div>
              );
            }
          })()}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
